import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { BillItemPendingModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { ProductDetailsModel } from '@data-layer/models/common';
import { enumBillItemUnit } from '@data-layer/enums/lcl';

@Component({
  selector: 'app-lcl-payment-items',
  template: `
    <div class="payment-breakdown-body">
      <div class="payment-breakdown-section" *ngIf="!isCollapsed">
        <h4>{{ chargesText | translate }}</h4>

        <accordion [isAnimated]="true">
          <accordion-group *ngIf="totalMasterProduct(originBillItems) !== 0">
            <div accordion-heading>
              <div class="d-flex justify-content-between">
                <div>
                  <span class="accordion-toggle-icon"></span>
                  <span>{{ 'LABELS.ORIGIN' | translate }}</span>
                </div>

                <div class="value highlighted">
                  {{ totalMasterProduct(originBillItems) | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                </div>
              </div>
            </div>

            <ng-container [ngTemplateOutlet]="billItemAccordionItem" [ngTemplateOutletContext]="{ items: originBillItems }"></ng-container>
          </accordion-group>

          <accordion-group *ngIf="totalMasterProduct(freightBillItems) !== 0">
            <div accordion-heading>
              <div class="d-flex justify-content-between">
                <div>
                  <span class="accordion-toggle-icon"></span>
                  <span>{{ 'LABELS.FREIGHT' | translate }}</span>
                </div>

                <div class="value highlighted">
                  {{ totalMasterProduct(freightBillItems) | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                </div>
              </div>
            </div>

            <ng-container [ngTemplateOutlet]="billItemAccordionItem" [ngTemplateOutletContext]="{ items: freightBillItems }"></ng-container>
          </accordion-group>

          <accordion-group *ngIf="totalMasterProduct(destinationBillItems) !== 0">
            <div accordion-heading>
              <div class="d-flex justify-content-between">
                <div>
                  <span class="accordion-toggle-icon"></span>
                  <span>{{ 'CFS_TYPE.DESTINATION' | translate }}</span>
                </div>

                <div class="value highlighted">
                  {{ totalMasterProduct(destinationBillItems) | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                </div>
              </div>
            </div>

            <ng-container
              [ngTemplateOutlet]="billItemAccordionItem"
              [ngTemplateOutletContext]="{ items: destinationBillItems }"
            ></ng-container>
          </accordion-group>

          <accordion-group *ngIf="totalMasterProduct(additionalBillItems) !== 0">
            <div accordion-heading>
              <div class="d-flex justify-content-between">
                <div>
                  <span class="accordion-toggle-icon"></span>
                  <span>{{ 'LABELS.ADDITIONAL_CHARGES' | translate }}</span>
                </div>

                <div class="value highlighted">
                  {{ totalMasterProduct(additionalBillItems) | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
                </div>
              </div>
            </div>

            <ng-container
              [ngTemplateOutlet]="billItemAccordionItem"
              [ngTemplateOutletContext]="{ items: additionalBillItems }"
            ></ng-container>
          </accordion-group>

          <ng-template #billItemAccordionItem let-items="items">
            <div class="row mb-2" *ngFor="let bill of items">
              <div class="col-12 label">{{ bill?.description }}</div>
              <div class="col-4 value">
                {{ bill?.internalQuantities?.quantity }} {{ billItemUnitName(bill?.internalQuantities?.unit) | translate }}
              </div>
              <div class="col-4 value text-center">
                {{ bill?.price || 0 | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
              </div>
              <div class="col-4 value highlighted text-right">
                {{ bill?.price * bill?.quantity || 0 | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
              </div>
            </div>

            <div class="row mb-2" *ngFor="let bill of discountBillItems(items)">
              <div class="col-8 label">{{ bill?.description }}</div>
              <div class="col-4 value highlighted text-right">
                {{ discountBillItemsValue(items) | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
              </div>
            </div>
          </ng-template>
        </accordion>
      </div>

      <div class="payment-breakdown-section" *ngIf="totalCollectionFee !== 0">
        <div class="row">
          <div class="col-8 label my-auto">
            {{ 'LABELS.COLLECTION_FEE' | translate }}
          </div>
          <div class="col-4 value highlighted text-right">
            {{ totalCollectionFee | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
          </div>
        </div>
      </div>

      <ng-container *ngIf="isShowAdditional">
        <div class="payment-breakdown-section">
          <div class="row row-summary">
            <div class="col-8 label my-auto">
              <p>{{ 'LABELS.ESTIMATED_SHIPPING_COST' | translate }}</p>
            </div>
            <div class="col-4 value text-right">
              {{ totalLCL | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
            </div>
          </div>
        </div>

        <div class="payment-breakdown-section optional" *ngIf="isShowAdditionalPricing && !isCollapsed">
          <h4>
            {{ 'LABELS.ADDITIONAL_SERVICES' | translate }}
            <span>({{ 'LABELS.WILL_BE_PAYED_LATER' | translate }})</span>
          </h4>

          <div class="row" *ngIf="isHasISF">
            <div class="col-8 label">{{ 'LCL_SHIPMENT.CUSTOM_OPTIONS.ISF' | translate }}</div>
            <div class="col-4 value highlighted text-right">
              {{ isfProductPrice | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
            </div>
          </div>

          <div class="row" *ngIf="isHasES">
            <div class="col-8 label">{{ 'LABELS.CUSTOMS_CLEARANCE_ENTRY_SUMMARY' | translate }}</div>
            <div class="col-4 value highlighted text-right">
              {{ esProductPrice | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
            </div>
          </div>

          <div class="row" *ngFor="let insuranceItem of joinedInsuranceItems">
            <div class="col-8 label">{{ insuranceItem?.description }}</div>
            <div class="col-4 value highlighted text-right">
              {{ insuranceItem?.price | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
            </div>
          </div>
        </div>

        <div class="payment-breakdown-section" *ngIf="isShowAdditionalPricing">
          <div class="row row-summary">
            <div class="col-8 label my-auto">
              <p>{{ 'LABELS.ADDITIONAL_COST' | translate }}</p>
            </div>
            <div class="col-4 value  text-right">
              {{ totalAdditional | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}
            </div>
          </div>
        </div>

        <div class="payment-breakdown-section is-last" *ngIf="isMobileView">
          <div class="row row-summary">
            <div class="col-8 label my-auto">
              <p>{{ 'LABELS.ESTIMATED_TOTAL_PRICE' | translate }}</p>
            </div>
            <div class="col-4 value text-right">
              <span class="total-sum">{{ totalSum | currency : DEFAULT_CURRENCY_CODE : 'symbol' : '1.2-2' }}</span>
            </div>
          </div>
        </div>

        <div class="payment-breakdown-warning" *ngIf="!isMobileView">
          <div><span class="icon-ic_big_a"></span></div>
          <div>
            <span>{{ 'LABELS.LEGAL_TERMS_DISCLAIMER_FOURTH_OPTION' | translate }}</span>
          </div>
        </div>

        <div class="payment-breakdown-hint-secondary" *ngIf="!isMobileView">
          <span>{{ 'LABELS.QUOTE_LCL_HINT_SECONDARY' | translate }}</span>
        </div>
      </ng-container>
    </div>
  `,
  styleUrls: ['./lcl-payment-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclPaymentItemsComponent {
  public _pendingBills: BillItemPendingModel[];

  public get pendingBills(): BillItemPendingModel[] {
    return this._pendingBills;
  }

  @Input()
  public set pendingBills(value: BillItemPendingModel[]) {
    this._pendingBills = value;
  }

  @Input() public pendingMarineInsuranceBills: PaymentBillItemPendingModel;
  @Input() public productsByCompany: ProductDetailsModel[];
  @Input() public isHasISF: boolean;
  @Input() public isHasES: boolean;
  @Input() public isBillLoading: boolean;
  @Input() public isBooking: boolean;
  @Input() public isMobileView: boolean;
  @Input() public isCollapsed: boolean;
  @Input() public isShowAdditional: boolean = true;
  @Input() public chargesText: string = 'LABELS.MAIN_CHARGES';

  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;

  private ORIGIN_BILL_ITEMS: enumBillItemProductCode[] = [
    enumBillItemProductCode.ORIGIN_CBM,
    enumBillItemProductCode.ORIGIN_HBL,
    enumBillItemProductCode.LOADING_FEES,
  ];

  private FREIGHT_BILL_ITEMS: enumBillItemProductCode[] = [enumBillItemProductCode.FREIGHT];

  private DESTINATION_BILL_ITEMS: enumBillItemProductCode[] = [
    enumBillItemProductCode.DESTINATION_CBM,
    enumBillItemProductCode.DESTINATION_HBL,
    enumBillItemProductCode.UNLOADING_FEES,
  ];

  private COLLECTION_FEE_BILL_ITEMS: enumBillItemProductCode[] = [enumBillItemProductCode.COLLECTION_FEE];

  private MASTER_BILL_ITEMS: enumBillItemProductCode[] = [
    enumBillItemProductCode.ORIGIN_CBM,
    enumBillItemProductCode.ORIGIN_HBL,
    enumBillItemProductCode.LOADING_FEES,
    enumBillItemProductCode.DESTINATION_CBM,
    enumBillItemProductCode.DESTINATION_HBL,
    enumBillItemProductCode.UNLOADING_FEES,
    enumBillItemProductCode.FREIGHT,
  ];

  constructor() {}

  public billItemUnitName(unit: enumBillItemUnit): string {
    switch (unit) {
      case enumBillItemUnit.CBM:
        return 'LENGTH_UNIT.CBM';
      case enumBillItemUnit.HBL:
        return 'LABELS.HBL';
      case enumBillItemUnit.TON:
        return 'WEIGHT_UNIT.TON';
      default:
        return '';
    }
  }

  public getSumGroup(group: BillItemPendingModel[]): number {
    const totalSum: number = (group || []).reduce((acc: number, current: BillItemPendingModel) => {
      return acc + current?.price * current?.quantity;
    }, 0);

    return totalSum + this.getSumDiscountGroup(group);
  }

  public getSumDiscountGroup(group: BillItemPendingModel[]): number {
    const totalDiscounts: number = (group || []).reduce((acc: number, current: BillItemPendingModel) => {
      return acc - current?.discountAmount;
    }, 0);

    return totalDiscounts;
  }

  public totalMasterProduct(items: BillItemPendingModel[]): number {
    return this.getSumGroup(items);
  }

  public discountBillItems(group: BillItemPendingModel[]): BillItemPendingModel[] {
    const groupDiscountGroupIds: string[] = (group || []).map((item: BillItemPendingModel) => item.discountGroupId);

    return (this.discountsBillItems?.items || []).filter((item: BillItemPendingModel) => {
      return groupDiscountGroupIds.includes(item.discountGroupId);
    });
  }

  public discountBillItemsValue(group: BillItemPendingModel[]): number {
    return this.getSumDiscountGroup(group);
  }

  public get mainBillItems(): BillItemPendingModel[] {
    return [...this.originBillItems, ...this.freightBillItems, ...this.destinationBillItems, ...this.additionalBillItems];
  }

  public get originBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills?.filter((item: BillItemPendingModel) => this.ORIGIN_BILL_ITEMS.includes(item.productCode) && item.price !== 0) || []
    );
  }

  public get freightBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills?.filter((item: BillItemPendingModel) => this.FREIGHT_BILL_ITEMS.includes(item.productCode) && item.price !== 0) ||
      []
    );
  }

  public get destinationBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills?.filter(
        (item: BillItemPendingModel) => this.DESTINATION_BILL_ITEMS.includes(item.productCode) && item.price !== 0,
      ) || []
    );
  }

  public get additionalBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills?.filter(
        (item: BillItemPendingModel) =>
          ![
            ...this.ORIGIN_BILL_ITEMS,
            ...this.FREIGHT_BILL_ITEMS,
            ...this.DESTINATION_BILL_ITEMS,
            ...this.COLLECTION_FEE_BILL_ITEMS,
            enumBillItemProductCode.DISCOUNT,
          ].includes(item.productCode) && item.price !== 0,
      ) || []
    );
  }

  public get collectionFeeBillItems(): BillItemPendingModel[] {
    return (
      this.pendingBills?.filter(
        (item: BillItemPendingModel) => this.COLLECTION_FEE_BILL_ITEMS.includes(item.productCode) && item.price !== 0,
      ) || []
    );
  }

  public get joinedInsuranceItems(): BillItemPendingModel[] {
    const nonCollectionFeeItems: BillItemPendingModel[] = (this.pendingMarineInsuranceBills?.items || []).filter(
      (paymentItem: BillItemPendingModel) => ![enumBillItemProductCode.COLLECTION_FEE].includes(paymentItem.productCode),
    );
    const collectionFeeItems: BillItemPendingModel[] =
      (this.pendingMarineInsuranceBills?.items || []).filter(
        (paymentItem: BillItemPendingModel) => paymentItem.productCode === enumBillItemProductCode.COLLECTION_FEE,
      ) || [];

    const result: BillItemPendingModel[] = [...nonCollectionFeeItems];
    if (collectionFeeItems?.length) {
      result.push({
        ...collectionFeeItems[0],
        quantity: 1,
        price: collectionFeeItems.reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0),
      });
    }

    return result;
  }

  public get isfProductPrice(): number {
    return (
      (this.productsByCompany || []).find((item: ProductDetailsModel) => item.productCode === enumBillItemProductCode.ISF)?.product
        ?.fixedPrice || 0
    );
  }

  public get esProductPrice(): number {
    return (
      (this.productsByCompany || []).find((item: ProductDetailsModel) => item.productCode === enumBillItemProductCode.BROKER_ENTRY)?.product
        ?.fixedPrice || 0
    );
  }

  public get totalLCL(): number {
    return (this.mainBillItems || []).reduce((acc: number, current: BillItemPendingModel) => acc + current.price * current.quantity, 0);
  }

  public get totalAdditional(): number {
    let result: number = 0;
    const totalMarineInsuranceValue: number = this.totalMarineInsurance;

    if (this.isHasISF) {
      result += this.isfProductPrice;
    }

    if (this.isHasES) {
      result += this.esProductPrice;
    }

    if (totalMarineInsuranceValue !== 0) {
      result += totalMarineInsuranceValue;
    }

    return result;
  }

  public get totalCollectionFee(): number {
    return (this.collectionFeeBillItems || []).reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0) || 0;
  }

  public get totalMarineInsurance(): number {
    return (this.pendingMarineInsuranceBills?.items || []).reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0) || 0;
  }

  public get totalSum(): number {
    return this.totalLCL + this.totalAdditional + this.totalCollectionFee || 0;
  }

  public get isShowAdditionalPricing(): boolean {
    return this.isHasISF || this.isHasES || this.totalMarineInsurance !== 0;
  }

  private get discountsBillItems(): { key: string; items: BillItemPendingModel[] } {
    return {
      key: 'DISCOUNTS',
      items: this.pendingBills?.filter(
        (item: BillItemPendingModel) =>
          !this.MASTER_BILL_ITEMS.includes(item.productCode) && item.productCode === enumBillItemProductCode.DISCOUNT,
      ),
    };
  }
}
